import React, {useId, useRef, useState} from "react";
import Modal from "./Modal";
import {C} from "../styles";
import {apiAdminLandingAiDraft} from "../api/landing";
import SECTION_FIELDS from "../landing/sectionFields.json";
import BLOCK_FIELDS from "../landing/blockFields.json";
import LANDING_AI from "../landing/landingAi.json";

const BRIEF_MIN = 3;
const BRIEF_MAX = 1500;
const TEXT_KINDS = ["text", "textarea", "title"];

const smallButton = {border: `1px solid ${C.border}`, background: "#fff", borderRadius: 6, padding: "4px 9px", cursor: "pointer", fontSize: 13};
const primaryButton = (disabled) => ({
    ...smallButton, background: disabled ? "#ccc" : C.primary, color: "#fff", border: "none", cursor: disabled ? "not-allowed" : "pointer",
});

const isTextSpec = (spec) => TEXT_KINDS.includes(spec.kind);
const isTextList = (spec) => spec.kind === "list" && Object.values(spec.itemFields || {}).length > 0
    && Object.values(spec.itemFields).every(isTextSpec);

// Registre des champs d'un type : rubrique intégrée (sectionFields.json) ou bloc libre (blockFields.json).
function registryFields(scope, type) {
    if (scope === "section") {
        if (LANDING_AI.excludedSectionTypes.includes(type)) return null;
        return SECTION_FIELDS[type] ? SECTION_FIELDS[type].fields : null;
    }
    if (scope === "block") {
        if (!LANDING_AI.draftBlockTypes.includes(type)) return null;
        return BLOCK_FIELDS.blocks[type] ? BLOCK_FIELDS.blocks[type].fields : null;
    }
    return null;
}

// Champs que l'assistant sait rédiger (textes et listes de textes) ; {} si le type n'est pas rédigeable.
// Même règle que draftableFields du serveur (la liste des blocs est testée contre le serveur : landingAi.mirror.test.js).
function draftableFields(scope, type) {
    const fields = registryFields(scope, type) || {};
    return Object.fromEntries(Object.entries(fields).filter(([, spec]) => isTextSpec(spec) || isTextList(spec)));
}

export const isDraftable = (scope, type) => Object.keys(draftableFields(scope, type)).length > 0;

const hasValue = (value) => (Array.isArray(value) ? value.length > 0 : typeof value === "string" && value !== "");

// Texte d'un élément de liste sur une ligne (sous-champs non vides, dans l'ordre du registre).
const itemLine = (spec, item) => Object.keys(spec.itemFields).map(k => item[k]).filter(v => typeof v === "string" && v !== "").join(" — ");

function ValueView({spec, value}) {
    if (Array.isArray(value)) {
        return (
            <ul style={{margin: 0, paddingLeft: 18}}>
                {value.map((item, i) => <li key={i} style={{whiteSpace: "pre-wrap"}}>{itemLine(spec, item)}</li>)}
            </ul>
        );
    }
    return <div style={{whiteSpace: "pre-wrap"}}>{value}</div>;
}

// Bouton « ✨ Proposer un texte » + fenêtre de brouillon IA. Ne fait que PROPOSER : le texte n'est appliqué (onApply, champs
// cochés seulement) qu'au clic sur « Appliquer la sélection », et jamais enregistré ici. Le texte de l'IA est affiché comme
// texte (jamais interprété comme du HTML).
export default function LandingAiDraft({scope, type, current = {}, disabled = false, onApply}) {
    const id = useId();
    const fields = draftableFields(scope, type);
    const busy = useRef(false);
    const [open, setOpen] = useState(false);
    const [brief, setBrief] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null); // {fields, note}
    const [checked, setChecked] = useState({});

    if (Object.keys(fields).length === 0) return null;

    const briefLength = brief.trim().length;
    const canGenerate = briefLength >= BRIEF_MIN && briefLength <= BRIEF_MAX && !loading;
    const proposedKeys = result ? Object.keys(fields).filter(k => hasValue(result.fields[k])) : [];
    const selectedKeys = proposedKeys.filter(k => checked[k]);

    function close() {
        setOpen(false);
        setBrief("");
        setError("");
        setResult(null);
        setChecked({});
    }

    async function generate() {
        if (busy.current || !canGenerate) return;
        busy.current = true; // verrou synchrone : un double clic n'envoie qu'une requête
        setLoading(true);
        setError("");
        setResult(null);
        const currentTexts = Object.fromEntries(Object.keys(fields).filter(k => hasValue(current[k])).map(k => [k, current[k]]));
        try {
            const res = await apiAdminLandingAiDraft({scope, type, brief: brief.trim(), current: currentTexts});
            if (!res || res.error) {
                setError((res && (res.message || res.error)) || "L'assistant n'a pas pu répondre.");
            } else if (!res.fields || !Object.keys(fields).some(k => hasValue(res.fields[k]))) {
                setError("L'assistant n'a rien proposé. Reformulez votre consigne.");
            } else {
                setResult({fields: res.fields, note: typeof res.note === "string" ? res.note : ""});
                setChecked(Object.fromEntries(Object.keys(fields).filter(k => hasValue(res.fields[k])).map(k => [k, true])));
            }
        } finally {
            busy.current = false;
            setLoading(false);
        }
    }

    function apply() {
        const picked = Object.fromEntries(selectedKeys.map(k => [k, result.fields[k]]));
        close();
        onApply(picked);
    }

    return (
        <div style={{marginBottom: 12}}>
            <button type="button" style={smallButton} disabled={disabled} aria-describedby={disabled ? `${id}-off` : undefined}
                    onClick={() => setOpen(true)}>✨ Proposer un texte</button>
            {disabled && (
                <span id={`${id}-off`} style={{marginLeft: 8, fontSize: 12, color: C.textMuted}}>
                    L'assistant IA n'est pas disponible sur ce serveur.
                </span>
            )}
            {open && (
                <Modal title="Proposer un texte" onClose={close} scrollable>
                    <p style={{marginTop: 0, fontSize: 13, color: C.textMuted}}>
                        L'assistant propose un texte, que vous relisez avant de l'appliquer. Rien n'est enregistré tant que vous ne cliquez pas sur « Enregistrer ».
                    </p>
                    <label htmlFor={`${id}-brief`} style={{display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4}}>
                        Que voulez-vous dire ?
                    </label>
                    <textarea id={`${id}-brief`} value={brief} maxLength={BRIEF_MAX} rows={4} disabled={loading}
                              onChange={e => setBrief(e.target.value)}
                              style={{width: "100%", boxSizing: "border-box", padding: "7px 9px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 14, fontFamily: "inherit"}}/>
                    <div style={{fontSize: 12, color: C.textMuted, textAlign: "right"}}>{brief.length} / {BRIEF_MAX}</div>
                    <div style={{display: "flex", justifyContent: "flex-end", margin: "8px 0"}}>
                        <button type="button" style={primaryButton(!canGenerate)} disabled={!canGenerate} onClick={generate}>
                            {loading ? "Génération en cours…" : "Générer"}
                        </button>
                    </div>
                    {error && <p role="alert" style={{color: C.danger, fontSize: 13, margin: "8px 0"}}>{error}</p>}

                    {result && (
                        <div>
                            <h4 style={{margin: "12px 0 6px"}}>Proposition</h4>
                            {result.note && <p style={{fontSize: 13, color: C.textMuted, margin: "0 0 8px", whiteSpace: "pre-wrap"}}>Note de l'assistant : {result.note}</p>}
                            {proposedKeys.map(key => {
                                const spec = fields[key];
                                return (
                                    <div key={key} style={{border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginBottom: 8}}>
                                        <label style={{display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14}}>
                                            <input type="checkbox" checked={Boolean(checked[key])}
                                                   onChange={e => setChecked(c => ({...c, [key]: e.target.checked}))}/>
                                            {spec.label}
                                        </label>
                                        {hasValue(current[key]) && (
                                            <div style={{marginTop: 6, fontSize: 13, color: C.textMuted}}>
                                                <div style={{fontSize: 12}}>Texte actuel</div>
                                                <ValueView spec={spec} value={current[key]}/>
                                            </div>
                                        )}
                                        <div style={{marginTop: 6, fontSize: 14}}>
                                            <div style={{fontSize: 12, color: C.textMuted}}>Proposition</div>
                                            <ValueView spec={spec} value={result.fields[key]}/>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div style={{display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12}}>
                        <button type="button" style={smallButton} onClick={close}>Annuler</button>
                        {result && (
                            <button type="button" style={primaryButton(selectedKeys.length === 0)} disabled={selectedKeys.length === 0} onClick={apply}>
                                Appliquer la sélection
                            </button>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
}
